import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield,
  Lock,
  Building2,
  Users,
  FileText,
  Phone,
  Settings,
  Save,
  Loader2,
  TrendingUp,
  DollarSign,
  CheckCircle2,
  AlertCircle,
  Clock,
  Eye,
  Tag,
  Plus,
  Trash2,
  X,
  Copy,
  Image,
  Link as LinkIcon,
  Upload,
  Send,
  Mail,
  BookOpen,
  Edit,
  Globe,
  Star,
  Fingerprint,
  Sparkles,
  ArrowUpRight,
  Home,
  Calculator
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APPLICATION_STATUSES = [
  { value: "pending", label: "Pending", color: "bg-yellow-100 text-yellow-700" },
  { value: "reviewed", label: "Reviewed", color: "bg-blue-100 text-blue-700" },
  { value: "contacted", label: "Contacted", color: "bg-purple-100 text-purple-700" },
  { value: "in_progress", label: "In Progress", color: "bg-cyan-100 text-cyan-700" },
  { value: "approved", label: "Approved", color: "bg-green-100 text-green-700" },
  { value: "declined", label: "Declined", color: "bg-red-100 text-red-700" },
  { value: "completed", label: "Completed", color: "bg-gray-100 text-gray-700" },
];

const DISCOUNT_TYPES = [
  { value: "percentage", label: "Percentage Off", example: "e.g., 50% off" },
  { value: "fixed", label: "Fixed Amount", example: "e.g., $5 off" },
  { value: "free", label: "100% Free", example: "No payment required" },
];

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Data states
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [leads, setLeads] = useState([]);
  const [bankConfig, setBankConfig] = useState([]);
  const [editingBank, setEditingBank] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedAppDetails, setSelectedAppDetails] = useState(null);
  const [loadingAppDetails, setLoadingAppDetails] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  
  // Promo code states
  const [promoCodes, setPromoCodes] = useState([]);
  const [showPromoDialog, setShowPromoDialog] = useState(false);
  const [newPromo, setNewPromo] = useState({
    code: "",
    discount_type: "percentage",
    discount_value: "",
    description: "",
    max_uses: "",
    expires_at: ""
  });
  const [creatingPromo, setCreatingPromo] = useState(false);
  const [deletingPromo, setDeletingPromo] = useState(null);

  // Bank Promotions (flyers) states
  const [bankPromotions, setBankPromotions] = useState([]);
  const [showBankPromoDialog, setShowBankPromoDialog] = useState(false);
  const [newBankPromo, setNewBankPromo] = useState({
    bank_name: "",
    title: "",
    image_url: "",
    link: "",
    display_order: 0
  });
  const [creatingBankPromo, setCreatingBankPromo] = useState(false);
  const [deletingBankPromo, setDeletingBankPromo] = useState(null);

  // Bank Connections states
  const [bankConnections, setBankConnections] = useState([]);
  const [updatingConnection, setUpdatingConnection] = useState(null);

  // Estimator config + broker call-backs
  const [estimatorConfig, setEstimatorConfig] = useState(null);
  const [estimatorDirty, setEstimatorDirty] = useState({});
  const [savingEstimator, setSavingEstimator] = useState(false);
  const [brokerCallbacks, setBrokerCallbacks] = useState([]);
  const [updatingCallback, setUpdatingCallback] = useState(null);
  const brokerCallbacksPending = brokerCallbacks.filter((c) => (c.status || "pending") === "pending").length;

  // Blog CMS states
  const [blogPosts, setBlogPosts] = useState([]);
  const [showBlogDialog, setShowBlogDialog] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [newBlogPost, setNewBlogPost] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    category: "Credit Education",
    featured_image: "",
    author: "Financify Team",
    is_published: false,
    meta_description: "",
    tags: []
  });
  const [savingPost, setSavingPost] = useState(false);
  const [deletingPost, setDeletingPost] = useState(null);

  // Reviews states
  const [reviews, setReviews] = useState([]);
  const [updatingReview, setUpdatingReview] = useState(null);

  // Documents states
  const [documents, setDocuments] = useState([]);
  const [updatingDocument, setUpdatingDocument] = useState(null);

  // Buying Power Leads
  const [buyingPowerLeads, setBuyingPowerLeads] = useState([]);
  const [buyingPowerStats, setBuyingPowerStats] = useState({ total: 0, paid: 0 });

  // Users states
  const [users, setUsers] = useState([]);
  const [usersStats, setUsersStats] = useState({ total: 0, verified: 0 });

  // Demo booking states
  const [demoAvailability, setDemoAvailability] = useState([]);
  const [demoBlockedDates, setDemoBlockedDates] = useState([]);
  const [demoBookings, setDemoBookings] = useState([]);
  const [newAvail, setNewAvail] = useState({ day_of_week: 1, start_time: "09:00", end_time: "17:00" });
  const [newBlockDate, setNewBlockDate] = useState("");
  const [savingAvail, setSavingAvail] = useState(false);
  const [deletingBooking, setDeletingBooking] = useState(null);

  // KYC states
  const [kycApplications, setKycApplications] = useState([]);
  const [kycFilter, setKycFilter] = useState("");

  // Credit Report states
  const [creditReports, setCreditReports] = useState([]);
  const [creditReportFilter, setCreditReportFilter] = useState("");
  const [sendingToBureau, setSendingToBureau] = useState(null);

  const handleLogin = async () => {
    setIsLoading(true);
    try {
      // Test auth by fetching config
      const response = await axios.get(`${API}/admin/config`, {
        headers: { "X-Admin-Password": password }
      });
      setBankConfig(response.data);
      setIsAuthenticated(true);
      sessionStorage.setItem("adminPassword", password);
      toast.success("Logged in successfully");
      loadDashboardData();
    } catch (error) {
      toast.error("Invalid password");
    } finally {
      setIsLoading(false);
    }
  };

  const getAuthHeader = () => ({
    headers: { "X-Admin-Password": sessionStorage.getItem("adminPassword") || password }
  });

  const loadDashboardData = async () => {
    try {
      const [statsRes, appsRes, leadsRes, configRes, promoRes, bankPromoRes, connectionsRes, blogRes, reviewsRes, documentsRes, usersRes, demoAvailRes, demoBookRes, kycRes, creditReportRes, bpLeadsRes, estimatorRes, callbacksRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, getAuthHeader()),
        axios.get(`${API}/admin/applications`, getAuthHeader()),
        axios.get(`${API}/admin/leads`, getAuthHeader()),
        axios.get(`${API}/admin/config`, getAuthHeader()),
        axios.get(`${API}/admin/promo`, getAuthHeader()),
        axios.get(`${API}/admin/promotions`, getAuthHeader()),
        axios.get(`${API}/admin/bank-connections`, getAuthHeader()),
        axios.get(`${API}/admin/blog/posts`, getAuthHeader()),
        axios.get(`${API}/admin/reviews`, getAuthHeader()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/documents`, getAuthHeader()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/users`, getAuthHeader()).catch(() => ({ data: { users: [], total: 0, verified_count: 0 } })),
        axios.get(`${API}/admin/demo-availability`, getAuthHeader()).catch(() => ({ data: { availability: [], blocked_dates: [] } })),
        axios.get(`${API}/admin/demo-bookings`, getAuthHeader()).catch(() => ({ data: { bookings: [] } })),
        axios.get(`${API}/admin/kyc-applications`, getAuthHeader()).catch(() => ({ data: { applications: [] } })),
        axios.get(`${API}/admin/credit-reports`, getAuthHeader()).catch(() => ({ data: { reports: [] } })),
        axios.get(`${API}/admin/buying-power-leads`, getAuthHeader()).catch(() => ({ data: { leads: [], total: 0, paid: 0 } })),
        axios.get(`${API}/estimator/config`).catch(() => ({ data: { config: null } })),
        axios.get(`${API}/admin/broker-callbacks`, getAuthHeader()).catch(() => ({ data: { callbacks: [], total: 0 } }))
      ]);
      setStats(statsRes.data);
      setApplications(appsRes.data.applications || []);
      setLeads(leadsRes.data.leads || []);
      setBankConfig(configRes.data);
      setPromoCodes(promoRes.data || []);
      setBankPromotions(bankPromoRes.data || []);
      setBankConnections(connectionsRes.data || []);
      setBlogPosts(blogRes.data || []);
      setReviews(reviewsRes.data || []);
      setDocuments(documentsRes.data || []);
      setUsers(usersRes.data.users || []);
      setUsersStats({ total: usersRes.data.total || 0, verified: usersRes.data.verified_count || 0 });
      setDemoAvailability(demoAvailRes.data.availability || []);
      setDemoBlockedDates(demoAvailRes.data.blocked_dates || []);
      setDemoBookings(demoBookRes.data.bookings || []);
      setKycApplications(kycRes.data.applications || []);
      setCreditReports(creditReportRes.data.reports || []);
      setBuyingPowerLeads(bpLeadsRes.data.leads || []);
      setBuyingPowerStats({ total: bpLeadsRes.data.total || 0, paid: bpLeadsRes.data.paid || 0 });
      setEstimatorConfig(estimatorRes.data?.config || null);
      setEstimatorDirty({});
      setBrokerCallbacks(callbacksRes.data?.callbacks || []);
    } catch (error) {
      console.error("Failed to load dashboard data", error);
    }
  };

  useEffect(() => {
    const savedPassword = sessionStorage.getItem("adminPassword");
    if (savedPassword) {
      setPassword(savedPassword);
      setIsAuthenticated(true);
      loadDashboardData();
    }
  }, []);

  const handleUpdateBank = async (bankId) => {
    if (!editingBank) return;
    
    setIsLoading(true);
    try {
      await axios.put(`${API}/admin/config/${bankId}`, editingBank, getAuthHeader());
      toast.success("Bank configuration updated");
      setEditingBank(null);
      loadDashboardData();
    } catch (error) {
      toast.error("Failed to update configuration");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatus = async (applicationId, newStatus) => {
    setUpdatingStatus(applicationId);
    try {
      await axios.put(
        `${API}/admin/applications/${applicationId}/status`,
        { application_status: newStatus },
        getAuthHeader()
      );
      toast.success("Application status updated");
      loadDashboardData();
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleSaveEstimatorConfig = async () => {
    if (!Object.keys(estimatorDirty).length) {
      toast.message("Nothing changed yet.");
      return;
    }
    setSavingEstimator(true);
    try {
      const res = await axios.put(`${API}/admin/estimator/config`, estimatorDirty, getAuthHeader());
      setEstimatorConfig(res.data.config);
      setEstimatorDirty({});
      toast.success(`Rates saved · version ${res.data.version}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save estimator rates.");
    } finally {
      setSavingEstimator(false);
    }
  };

  const handleUpdateCallbackStatus = async (id, status) => {
    setUpdatingCallback(id);
    try {
      await axios.put(`${API}/admin/broker-callbacks/${id}`, { status }, getAuthHeader());
      setBrokerCallbacks((cur) => cur.map((c) => (c.id === id ? { ...c, status } : c)));
      toast.success(`Call-back marked ${status.replace("_", " ")}`);
    } catch (e) {
      toast.error("Could not update call-back.");
    } finally {
      setUpdatingCallback(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = APPLICATION_STATUSES.find(s => s.value === status) || APPLICATION_STATUSES[0];
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
        {statusConfig.label}
      </span>
    );
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'BSD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const viewApplicationDetails = async (app) => {
    setSelectedApp(app);
    setLoadingAppDetails(true);
    try {
      const response = await axios.get(`${API}/admin/applications/${app.id}`, getAuthHeader());
      setSelectedAppDetails(response.data);
    } catch (error) {
      console.error("Failed to load application details", error);
      // Still show basic details from the list
      setSelectedAppDetails({ application: app, user_info: null, documents: [] });
    } finally {
      setLoadingAppDetails(false);
    }
  };

  const downloadDocument = (docId, fileName) => {
    const url = `${API}/admin/documents/${docId}/download`;
    const adminPwd = sessionStorage.getItem("adminPassword") || password;
    fetch(url, { headers: { 'X-Admin-Password': adminPwd } })
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.blob();
      })
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', fileName || 'document');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(err => {
        console.error("Download failed", err);
        toast.error("Failed to download document. Please re-login.");
      });
  };

  const viewDocument = (docId) => {
    const url = `${API}/admin/documents/${docId}/download`;
    const adminPwd = sessionStorage.getItem("adminPassword") || password;
    fetch(url, { headers: { 'X-Admin-Password': adminPwd } })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
      })
      .catch(() => toast.error("Failed to open document"));
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="card-base max-w-md w-full" data-testid="admin-login">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#52BFB8]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-[#52BFB8]" />
            </div>
            <h1 className="heading-3">Admin Access</h1>
            <p className="text-gray-500 mt-2">Enter password to access the admin panel</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                  className="pl-10"
                  placeholder="Enter admin password"
                  data-testid="admin-password-input"
                />
              </div>
            </div>
            <Button 
              className="w-full btn-primary" 
              onClick={handleLogin}
              disabled={isLoading || !password}
              data-testid="admin-login-btn"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Access Admin Panel"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  const NAV_GROUPS = [
    {
      label: "Overview",
      items: [
        { key: "dashboard", label: "Dashboard", icon: TrendingUp },
      ],
    },
    {
      label: "Operations",
      items: [
        { key: "applications", label: "Applications", icon: FileText },
        { key: "leads", label: "Leads", icon: Phone },
        { key: "users", label: "Users", icon: Users, badge: usersStats.total, badgeColor: "bg-gray-500" },
        { key: "connections", label: "Bank Requests", icon: Send, badge: bankConnections.filter(c => c.status === "pending").length, badgeColor: "bg-red-500" },
      ],
    },
    {
      label: "Verification",
      items: [
        { key: "documents", label: "Documents", icon: Upload, badge: documents.filter(d => d.status === "pending").length, badgeColor: "bg-blue-500" },
        { key: "kyc", label: "KYC", icon: Fingerprint, badge: kycApplications.length, badgeColor: "bg-purple-500" },
        { key: "credit-reports", label: "Credit Reports", icon: FileText, badge: creditReports.length, badgeColor: "bg-orange-500" },
      ],
    },
    {
      label: "Marketing",
      items: [
        { key: "blog", label: "Blog CMS", icon: BookOpen },
        { key: "promo", label: "Promo Codes", icon: Tag },
        { key: "bank-promos", label: "Bank Promos", icon: Image },
        { key: "reviews", label: "Reviews", icon: Star, badge: reviews.filter(r => r.status === "pending").length, badgeColor: "bg-amber-500" },
        { key: "buying-power-leads", label: "Buying Power Leads", icon: Home, badge: buyingPowerStats.total, badgeColor: "bg-[#52BFB8]" },
      ],
    },
    {
      label: "Bookings",
      items: [
        { key: "demo-slots", label: "Demo Slots", icon: Clock, badge: demoBookings.length, badgeColor: "bg-[#52BFB8]" },
        { key: "sales-demos", label: "Sales Demos", icon: Sparkles },
      ],
    },
    {
      label: "Settings",
      items: [
        { key: "config", label: "Bank Config", icon: Settings },
        { key: "estimator", label: "Estimator Rates", icon: Calculator },
        { key: "broker-callbacks", label: "Broker Call-backs", icon: Phone, badge: brokerCallbacksPending, badgeColor: "bg-amber-500" },
      ],
    },
  ];

  const allItems = NAV_GROUPS.flatMap((g) => g.items);
  const activeItem = allItems.find((i) => i.key === activeTab) || allItems[0];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex flex-col lg:flex-row">
        {/* Sidebar */}
        <aside className="lg:w-64 lg:min-h-screen bg-white border-r border-gray-200 lg:sticky lg:top-0 flex-shrink-0" data-testid="admin-sidebar">
          <div className="px-5 py-6 border-b border-gray-100">
            <p className="text-lg font-bold text-[#2D3748]">Admin Panel</p>
            <p className="text-xs text-gray-500 mt-1">Financify Caribbean</p>
          </div>

          {/* Mobile dropdown to switch tabs (replaces sidebar on small screens) */}
          <div className="lg:hidden px-5 py-3 border-b border-gray-100">
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#52BFB8]/30"
              data-testid="admin-mobile-tab-select"
            >
              {NAV_GROUPS.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.items.map((i) => (
                    <option key={i.key} value={i.key}>
                      {i.label}{i.badge ? ` (${i.badge})` : ""}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <nav className="hidden lg:block px-3 py-4 space-y-5">
            {NAV_GROUPS.map((group) => (
              <div key={group.label}>
                <p className="px-3 text-[10px] uppercase tracking-[0.18em] text-gray-400 font-semibold mb-2">
                  {group.label}
                </p>
                <div className="space-y-0.5">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const active = item.key === activeTab;
                    return (
                      <button
                        key={item.key}
                        onClick={() => setActiveTab(item.key)}
                        data-testid={`tab-${item.key}`}
                        className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                          active
                            ? "bg-[#52BFB8]/10 text-[#52BFB8] font-semibold"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        <span className="flex items-center gap-2.5 min-w-0">
                          <Icon className={`w-4 h-4 flex-shrink-0 ${active ? "text-[#52BFB8]" : "text-gray-400"}`} />
                          <span className="truncate">{item.label}</span>
                        </span>
                        {item.badge ? (
                          <span className={`px-1.5 py-0.5 text-[10px] font-semibold text-white rounded-full flex-shrink-0 ${item.badgeColor || "bg-gray-500"}`}>
                            {item.badge}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          <div className="mt-auto p-4 lg:absolute lg:bottom-0 lg:left-0 lg:right-0 lg:w-64 border-t border-gray-100 bg-white">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => {
                sessionStorage.removeItem("adminPassword");
                setIsAuthenticated(false);
                setPassword("");
              }}
              data-testid="admin-logout"
            >
              Logout
            </Button>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0">
          <div className="px-5 lg:px-10 py-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-400 mb-1">{activeItem ? NAV_GROUPS.find(g => g.items.some(i => i.key === activeItem.key))?.label : ""}</p>
                <h1 className="heading-2 flex items-center gap-3">
                  {activeItem?.icon && <activeItem.icon className="w-7 h-7 text-[#52BFB8]" />}
                  {activeItem?.label || "Dashboard"}
                </h1>
              </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              {/* TabsList is hidden — navigation lives in sidebar */}
              <TabsList className="hidden">
                {allItems.map((i) => (
                  <TabsTrigger key={i.key} value={i.key} />
                ))}
              </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            {stats && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="card-base">
                  <div className="flex items-center gap-3 mb-2">
                    <FileText className="w-5 h-5 text-[#52BFB8]" />
                    <span className="text-sm text-gray-500">Total Applications</span>
                  </div>
                  <p className="text-3xl font-bold text-[#4A4A4A]">{stats.total_applications}</p>
                </div>
                <div className="card-base">
                  <div className="flex items-center gap-3 mb-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    <span className="text-sm text-gray-500">Qualified</span>
                  </div>
                  <p className="text-3xl font-bold text-[#4A4A4A]">{stats.qualified_applications}</p>
                </div>
                <div className="card-base">
                  <div className="flex items-center gap-3 mb-2">
                    <TrendingUp className="w-5 h-5 text-[#E8B84A]" />
                    <span className="text-sm text-gray-500">Qualification Rate</span>
                  </div>
                  <p className="text-3xl font-bold text-[#4A4A4A]">{stats.qualification_rate}%</p>
                </div>
                <div className="card-base">
                  <div className="flex items-center gap-3 mb-2">
                    <Phone className="w-5 h-5 text-[#52BFB8]" />
                    <span className="text-sm text-gray-500">Callback Requests</span>
                  </div>
                  <p className="text-3xl font-bold text-[#4A4A4A]">{stats.callback_requests}</p>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <div className="card-base overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Qualification</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Requested</TableHead>
                    <TableHead>TDSR</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applications.map((app) => (
                    <TableRow key={app.id}>
                      <TableCell className="font-medium">{app.applicant_name}</TableCell>
                      <TableCell>{app.applicant_email}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          app.status === "qualified" 
                            ? "bg-green-100 text-green-700" 
                            : "bg-red-100 text-red-700"
                        }`}>
                          {app.status === "qualified" ? "Qualified" : "Not Qualified"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={app.application_status || "pending"}
                          onValueChange={(value) => handleUpdateStatus(app.id, value)}
                          disabled={updatingStatus === app.id}
                        >
                          <SelectTrigger className="w-[130px] h-8 text-xs">
                            <SelectValue>
                              {updatingStatus === app.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                getStatusBadge(app.application_status || "pending")
                              )}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {APPLICATION_STATUSES.map((status) => (
                              <SelectItem key={status.value} value={status.value}>
                                <span className={`px-2 py-1 rounded-full text-xs ${status.color}`}>
                                  {status.label}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>{formatCurrency(app.requested_amount)}</TableCell>
                      <TableCell>{app.tdsr_percentage}%</TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {formatDate(app.created_at)}
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => viewApplicationDetails(app)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {applications.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-gray-500 py-8">
                        No applications yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Application Detail Dialog */}
            <Dialog open={!!selectedApp} onOpenChange={() => { setSelectedApp(null); setSelectedAppDetails(null); }}>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Application Details</DialogTitle>
                  <DialogDescription>
                    Application ID: {selectedApp?.id}
                  </DialogDescription>
                </DialogHeader>
                {loadingAppDetails ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[#52BFB8]" />
                  </div>
                ) : selectedApp && (
                  <div className="space-y-6 mt-4">
                    {/* Basic Info */}
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Applicant Information
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
                        <div>
                          <Label className="text-gray-500 text-xs">Full Name</Label>
                          <p className="font-medium">{selectedApp.applicant_name}</p>
                        </div>
                        <div>
                          <Label className="text-gray-500 text-xs">Email</Label>
                          <p className="font-medium">{selectedApp.applicant_email}</p>
                        </div>
                        <div>
                          <Label className="text-gray-500 text-xs">Phone</Label>
                          <p className="font-medium">{selectedApp.applicant_phone || "N/A"}</p>
                        </div>
                      </div>
                    </div>

                    {/* Application Form Data */}
                    {(selectedAppDetails?.application?.application_data || selectedApp.application_data) && (
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Application Form Data
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-blue-50 p-4 rounded-lg text-sm">
                          {Object.entries(selectedAppDetails?.application?.application_data || selectedApp.application_data || {}).map(([key, value]) => {
                            if (value === null || value === undefined || value === '' || (typeof value === 'object' && Object.keys(value).length === 0)) return null;
                            const label = key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                            const displayValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
                            return (
                              <div key={key}>
                                <Label className="text-gray-500 text-xs">{label}</Label>
                                <p className="font-medium text-gray-800">{displayValue}</p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Financial Summary */}
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <DollarSign className="w-4 h-4" />
                        Financial Summary
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-green-50 p-4 rounded-lg">
                        <div>
                          <Label className="text-gray-500 text-xs">Requested Amount</Label>
                          <p className="font-medium">{formatCurrency(selectedApp.requested_amount)}</p>
                        </div>
                        <div>
                          <Label className="text-gray-500 text-xs">Monthly Income</Label>
                          <p className="font-medium">{formatCurrency(selectedApp.total_monthly_income)}</p>
                        </div>
                        <div>
                          <Label className="text-gray-500 text-xs">Monthly Debt</Label>
                          <p className="font-medium">{formatCurrency(selectedApp.total_monthly_debt)}</p>
                        </div>
                        <div>
                          <Label className="text-gray-500 text-xs">TDSR</Label>
                          <p className="font-medium">{selectedApp.tdsr_percentage}%</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Matched Banks */}
                    {selectedApp.matched_banks?.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          Matched Banks
                        </h3>
                        <div className="space-y-2">
                          {selectedApp.matched_banks.map((bank, i) => (
                            <div key={i} className="bg-gray-50 p-3 rounded-lg text-sm flex justify-between items-center">
                              <div>
                                <p className="font-medium">{bank.bank_name}</p>
                                <p className="text-gray-600">
                                  Amount: {formatCurrency(bank.max_approved_amount)} | 
                                  Rate: {bank.interest_rate}% |
                                  Term: {bank.max_term_years}yr
                                </p>
                              </div>
                              <p className="text-lg font-semibold text-green-600">
                                {formatCurrency(bank.estimated_monthly_payment)}/mo
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Decline Reasons */}
                    {selectedApp.decline_reasons?.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-red-500" />
                          Decline Reasons
                        </h3>
                        <ul className="list-disc list-inside text-sm text-gray-600 bg-red-50 p-4 rounded-lg">
                          {selectedApp.decline_reasons.map((reason, i) => (
                            <li key={i}>{reason}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Uploaded Documents */}
                    {selectedAppDetails?.documents?.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <Upload className="w-4 h-4" />
                          Uploaded Documents
                        </h3>
                        <div className="space-y-2">
                          {selectedAppDetails.documents.map((doc, i) => (
                            <div key={i} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                              <div className="flex items-center gap-3">
                                <FileText className="w-5 h-5 text-gray-400" />
                                <div>
                                  <p className="font-medium capitalize">{doc.document_type?.replace(/_/g, ' ')}</p>
                                  <p className="text-xs text-gray-500">{doc.file_name}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  doc.status === 'verified' ? 'bg-green-100 text-green-700' :
                                  doc.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                  'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {doc.status}
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => downloadDocument(doc.id, doc.file_name)}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  View
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Leads Tab */}
          <TabsContent value="leads">
            <div className="card-base overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Preferred Time</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((lead) => (
                    <TableRow key={lead.id}>
                      <TableCell className="font-medium">{lead.full_name}</TableCell>
                      <TableCell>{lead.email}</TableCell>
                      <TableCell>{lead.phone || "-"}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          lead.request_type === "callback" 
                            ? "bg-blue-100 text-blue-700" 
                            : "bg-purple-100 text-purple-700"
                        }`}>
                          {lead.request_type === "callback" ? "Callback" : "Email"}
                        </span>
                      </TableCell>
                      <TableCell>{lead.preferred_time || "-"}</TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {formatDate(lead.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {leads.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                        No leads yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Bank Config Tab */}
          <TabsContent value="config">
            <div className="space-y-6">
              {bankConfig.map((bank) => (
                <div key={bank.bank_id} className="card-base">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      {bank.bank_logo && (
                        <img src={bank.bank_logo} alt={bank.bank_name} className="h-10 w-auto" />
                      )}
                      <div>
                        <h3 className="font-semibold text-[#4A4A4A]">{bank.bank_name}</h3>
                        <p className="text-sm text-gray-500">Bank ID: {bank.bank_id}</p>
                      </div>
                    </div>
                    {editingBank?.bank_id !== bank.bank_id ? (
                      <Button 
                        variant="outline" 
                        onClick={() => setEditingBank({...bank})}
                        data-testid={`edit-bank-${bank.bank_id}`}
                      >
                        Edit
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          onClick={() => setEditingBank(null)}
                        >
                          Cancel
                        </Button>
                        <Button 
                          className="btn-primary"
                          onClick={() => handleUpdateBank(bank.bank_id)}
                          disabled={isLoading}
                          data-testid={`save-bank-${bank.bank_id}`}
                        >
                          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save</>}
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Government Workers */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-[#4A4A4A] font-medium">
                        <Building2 className="w-5 h-5" />
                        Government Workers
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Max Loan</Label>
                          {editingBank?.bank_id === bank.bank_id ? (
                            <Input
                              type="number"
                              value={editingBank.gov_max_loan}
                              onChange={(e) => setEditingBank({...editingBank, gov_max_loan: Number(e.target.value)})}
                              className="mt-1"
                            />
                          ) : (
                            <p className="font-semibold">{formatCurrency(bank.gov_max_loan)}</p>
                          )}
                        </div>
                        <div>
                          <Label>Max TDSR</Label>
                          {editingBank?.bank_id === bank.bank_id ? (
                            <Input
                              type="number"
                              value={editingBank.gov_max_tdsr}
                              onChange={(e) => setEditingBank({...editingBank, gov_max_tdsr: Number(e.target.value)})}
                              className="mt-1"
                            />
                          ) : (
                            <p className="font-semibold">{bank.gov_max_tdsr}%</p>
                          )}
                        </div>
                        <div>
                          <Label>Min Employment (years)</Label>
                          {editingBank?.bank_id === bank.bank_id ? (
                            <Input
                              type="number"
                              value={editingBank.gov_min_employment_years}
                              onChange={(e) => setEditingBank({...editingBank, gov_min_employment_years: Number(e.target.value)})}
                              className="mt-1"
                            />
                          ) : (
                            <p className="font-semibold">{bank.gov_min_employment_years}</p>
                          )}
                        </div>
                        <div>
                          <Label>Max Term (years)</Label>
                          {editingBank?.bank_id === bank.bank_id ? (
                            <Input
                              type="number"
                              value={editingBank.gov_max_term_years}
                              onChange={(e) => setEditingBank({...editingBank, gov_max_term_years: Number(e.target.value)})}
                              className="mt-1"
                            />
                          ) : (
                            <p className="font-semibold">{bank.gov_max_term_years}</p>
                          )}
                        </div>
                        <div className="col-span-2">
                          <Label>Max Age at Maturity</Label>
                          {editingBank?.bank_id === bank.bank_id ? (
                            <Input
                              type="number"
                              value={editingBank.gov_max_age_at_maturity}
                              onChange={(e) => setEditingBank({...editingBank, gov_max_age_at_maturity: Number(e.target.value)})}
                              className="mt-1"
                            />
                          ) : (
                            <p className="font-semibold">{bank.gov_max_age_at_maturity}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Private Workers */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-[#4A4A4A] font-medium">
                        <Users className="w-5 h-5" />
                        Private Company Employees
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Max Loan</Label>
                          {editingBank?.bank_id === bank.bank_id ? (
                            <Input
                              type="number"
                              value={editingBank.private_max_loan}
                              onChange={(e) => setEditingBank({...editingBank, private_max_loan: Number(e.target.value)})}
                              className="mt-1"
                            />
                          ) : (
                            <p className="font-semibold">{formatCurrency(bank.private_max_loan)}</p>
                          )}
                        </div>
                        <div>
                          <Label>Max TDSR</Label>
                          {editingBank?.bank_id === bank.bank_id ? (
                            <Input
                              type="number"
                              value={editingBank.private_max_tdsr}
                              onChange={(e) => setEditingBank({...editingBank, private_max_tdsr: Number(e.target.value)})}
                              className="mt-1"
                            />
                          ) : (
                            <p className="font-semibold">{bank.private_max_tdsr}%</p>
                          )}
                        </div>
                        <div>
                          <Label>Min Employment (years)</Label>
                          {editingBank?.bank_id === bank.bank_id ? (
                            <Input
                              type="number"
                              value={editingBank.private_min_employment_years}
                              onChange={(e) => setEditingBank({...editingBank, private_min_employment_years: Number(e.target.value)})}
                              className="mt-1"
                            />
                          ) : (
                            <p className="font-semibold">{bank.private_min_employment_years}</p>
                          )}
                        </div>
                        <div>
                          <Label>Max Term (years)</Label>
                          {editingBank?.bank_id === bank.bank_id ? (
                            <Input
                              type="number"
                              value={editingBank.private_max_term_years}
                              onChange={(e) => setEditingBank({...editingBank, private_max_term_years: Number(e.target.value)})}
                              className="mt-1"
                            />
                          ) : (
                            <p className="font-semibold">{bank.private_max_term_years}</p>
                          )}
                        </div>
                        <div className="col-span-2">
                          <Label>Max Age at Maturity</Label>
                          {editingBank?.bank_id === bank.bank_id ? (
                            <Input
                              type="number"
                              value={editingBank.private_max_age_at_maturity}
                              onChange={(e) => setEditingBank({...editingBank, private_max_age_at_maturity: Number(e.target.value)})}
                              className="mt-1"
                            />
                          ) : (
                            <p className="font-semibold">{bank.private_max_age_at_maturity}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Common Settings */}
                  <div className="mt-6 pt-6 border-t">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <Label>Interest Rate</Label>
                        {editingBank?.bank_id === bank.bank_id ? (
                          <Input
                            type="number"
                            step="0.1"
                            value={editingBank.interest_rate}
                            onChange={(e) => setEditingBank({...editingBank, interest_rate: Number(e.target.value)})}
                            className="mt-1"
                          />
                        ) : (
                          <p className="font-semibold">{bank.interest_rate}%</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Promo Codes Tab */}
          <TabsContent value="promo">
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-[#4A4A4A]">Promo Codes</h2>
                  <p className="text-sm text-gray-500">Create and manage discount codes</p>
                </div>
                <Button 
                  onClick={() => setShowPromoDialog(true)}
                  className="btn-primary"
                  data-testid="create-promo-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Code
                </Button>
              </div>

              {/* Promo Codes Table */}
              <div className="bg-white rounded-xl border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Discount</TableHead>
                      <TableHead>Usage</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {promoCodes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                          No promo codes yet. Create your first one!
                        </TableCell>
                      </TableRow>
                    ) : (
                      promoCodes.map((promo) => {
                        const isExpired = promo.expires_at && new Date(promo.expires_at) < new Date();
                        const isMaxedOut = promo.max_uses && promo.current_uses >= promo.max_uses;
                        const isActive = promo.is_active && !isExpired && !isMaxedOut;
                        
                        return (
                          <TableRow key={promo.code}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span className="font-mono font-semibold text-[#52BFB8]">{promo.code}</span>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(promo.code);
                                    toast.success("Code copied!");
                                  }}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                              </div>
                              {promo.description && (
                                <p className="text-xs text-gray-500">{promo.description}</p>
                              )}
                            </TableCell>
                            <TableCell>
                              <span className="capitalize">{promo.discount_type}</span>
                            </TableCell>
                            <TableCell>
                              {promo.discount_type === "free" 
                                ? "100% OFF" 
                                : promo.discount_type === "percentage"
                                  ? `${promo.discount_value}%`
                                  : `$${promo.discount_value}`
                              }
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <span className="font-medium">{promo.current_uses || 0}</span>
                                {promo.max_uses ? (
                                  <span className="text-gray-500"> / {promo.max_uses}</span>
                                ) : (
                                  <span className="text-gray-500"> / ∞</span>
                                )}
                              </div>
                              {promo.max_uses && (
                                <div className="w-20 h-1.5 bg-gray-200 rounded-full mt-1">
                                  <div 
                                    className="h-full bg-[#52BFB8] rounded-full"
                                    style={{ width: `${Math.min((promo.current_uses / promo.max_uses) * 100, 100)}%` }}
                                  />
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              {promo.expires_at ? (
                                <span className={isExpired ? "text-red-500" : ""}>
                                  {new Date(promo.expires_at).toLocaleDateString()}
                                </span>
                              ) : (
                                <span className="text-gray-400">Never</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                isActive 
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                              }`}>
                                {isActive ? "Active" : isExpired ? "Expired" : isMaxedOut ? "Maxed Out" : "Inactive"}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={async () => {
                                    try {
                                      await axios.put(
                                        `${API}/admin/promo/${promo.code}?is_active=${!promo.is_active}`,
                                        {},
                                        getAuthHeader()
                                      );
                                      toast.success(`Code ${promo.is_active ? "deactivated" : "activated"}`);
                                      loadDashboardData();
                                    } catch (error) {
                                      toast.error("Failed to update code");
                                    }
                                  }}
                                  className={promo.is_active ? "text-orange-600" : "text-green-600"}
                                >
                                  {promo.is_active ? "Disable" : "Enable"}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={async () => {
                                    if (!confirm(`Delete promo code ${promo.code}?`)) return;
                                    setDeletingPromo(promo.code);
                                    try {
                                      await axios.delete(`${API}/admin/promo/${promo.code}`, getAuthHeader());
                                      toast.success("Promo code deleted");
                                      loadDashboardData();
                                    } catch (error) {
                                      toast.error("Failed to delete code");
                                    } finally {
                                      setDeletingPromo(null);
                                    }
                                  }}
                                  disabled={deletingPromo === promo.code}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  {deletingPromo === promo.code ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Create Promo Dialog */}
            <Dialog open={showPromoDialog} onOpenChange={setShowPromoDialog}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Create Promo Code</DialogTitle>
                  <DialogDescription>
                    Create a new discount code for customers
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label htmlFor="promo-code">Code *</Label>
                    <Input
                      id="promo-code"
                      placeholder="e.g., LAUNCH50"
                      value={newPromo.code}
                      onChange={(e) => setNewPromo({...newPromo, code: e.target.value.toUpperCase()})}
                      className="mt-1 font-mono"
                      data-testid="promo-code-field"
                    />
                    <p className="text-xs text-gray-500 mt-1">Letters and numbers only, no spaces</p>
                  </div>

                  <div>
                    <Label htmlFor="discount-type">Discount Type *</Label>
                    <Select 
                      value={newPromo.discount_type}
                      onValueChange={(value) => setNewPromo({...newPromo, discount_type: value, discount_value: value === "free" ? "" : newPromo.discount_value})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DISCOUNT_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            <div>
                              <span>{type.label}</span>
                              <span className="text-xs text-gray-500 ml-2">{type.example}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {newPromo.discount_type !== "free" && (
                    <div>
                      <Label htmlFor="discount-value">
                        {newPromo.discount_type === "percentage" ? "Percentage Off *" : "Amount Off ($) *"}
                      </Label>
                      <Input
                        id="discount-value"
                        type="number"
                        placeholder={newPromo.discount_type === "percentage" ? "e.g., 50" : "e.g., 5.00"}
                        value={newPromo.discount_value}
                        onChange={(e) => setNewPromo({...newPromo, discount_value: e.target.value})}
                        className="mt-1"
                        min="0"
                        max={newPromo.discount_type === "percentage" ? "100" : "19.99"}
                      />
                    </div>
                  )}

                  <div>
                    <Label htmlFor="description">Description (optional)</Label>
                    <Input
                      id="description"
                      placeholder="e.g., Launch special for early users"
                      value={newPromo.description}
                      onChange={(e) => setNewPromo({...newPromo, description: e.target.value})}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="max-uses">Usage Limit (optional)</Label>
                    <Input
                      id="max-uses"
                      type="number"
                      placeholder="e.g., 10 (leave empty for unlimited)"
                      value={newPromo.max_uses}
                      onChange={(e) => setNewPromo({...newPromo, max_uses: e.target.value})}
                      className="mt-1"
                      min="1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Maximum number of times this code can be used</p>
                  </div>

                  <div>
                    <Label htmlFor="expires-at">Expiry Date (optional)</Label>
                    <Input
                      id="expires-at"
                      type="date"
                      value={newPromo.expires_at}
                      onChange={(e) => setNewPromo({...newPromo, expires_at: e.target.value})}
                      className="mt-1"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setShowPromoDialog(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      if (!newPromo.code.trim()) {
                        toast.error("Please enter a code");
                        return;
                      }
                      if (newPromo.discount_type !== "free" && !newPromo.discount_value) {
                        toast.error("Please enter a discount value");
                        return;
                      }
                      
                      setCreatingPromo(true);
                      try {
                        const params = new URLSearchParams({
                          code: newPromo.code.trim(),
                          discount_type: newPromo.discount_type,
                        });
                        if (newPromo.discount_value) params.append("discount_value", newPromo.discount_value);
                        if (newPromo.description) params.append("description", newPromo.description);
                        if (newPromo.max_uses) params.append("max_uses", newPromo.max_uses);
                        if (newPromo.expires_at) params.append("expires_at", newPromo.expires_at);
                        
                        await axios.post(`${API}/admin/promo?${params.toString()}`, {}, getAuthHeader());
                        toast.success(`Promo code ${newPromo.code} created!`);
                        setShowPromoDialog(false);
                        setNewPromo({ code: "", discount_type: "percentage", discount_value: "", description: "", max_uses: "", expires_at: "" });
                        loadDashboardData();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || "Failed to create code");
                      } finally {
                        setCreatingPromo(false);
                      }
                    }}
                    className="btn-primary"
                    disabled={creatingPromo}
                  >
                    {creatingPromo ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Create Code
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Bank Promotions Tab */}
          <TabsContent value="bank-promos">
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-[#4A4A4A]">Bank Promotions</h2>
                  <p className="text-sm text-gray-500">Manage promotional banners shown on the homepage carousel</p>
                </div>
                <Button 
                  onClick={() => setShowBankPromoDialog(true)}
                  className="btn-primary"
                  data-testid="create-bank-promo-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Promotion
                </Button>
              </div>

              {/* Bank Promotions Grid */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {bankPromotions.length === 0 ? (
                  <div className="col-span-full bg-white rounded-xl border p-8 text-center">
                    <Image className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500">No bank promotions yet. Add your first one!</p>
                  </div>
                ) : (
                  bankPromotions.map((promo) => (
                    <div 
                      key={promo.id} 
                      className={`bg-white rounded-xl border overflow-hidden ${
                        !promo.is_active ? "opacity-60" : ""
                      }`}
                    >
                      {/* Image Preview */}
                      <div className="aspect-[16/9] bg-gray-100 relative">
                        {promo.image_url ? (
                          <img 
                            src={promo.image_url} 
                            alt={promo.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className="absolute inset-0 flex items-center justify-center text-gray-400" style={{ display: promo.image_url ? 'none' : 'flex' }}>
                          <Image className="w-10 h-10" />
                        </div>
                        {/* Order Badge */}
                        <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                          Order: {promo.display_order}
                        </div>
                        {/* Active Status */}
                        <div className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium ${
                          promo.is_active ? "bg-green-500 text-white" : "bg-gray-500 text-white"
                        }`}>
                          {promo.is_active ? "Active" : "Inactive"}
                        </div>
                      </div>
                      
                      {/* Details */}
                      <div className="p-4">
                        <h3 className="font-semibold text-[#4A4A4A] mb-1">{promo.title}</h3>
                        <p className="text-sm text-gray-500 mb-3">{promo.bank_name}</p>
                        
                        {promo.link && (
                          <a 
                            href={promo.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-[#52BFB8] hover:underline flex items-center gap-1 mb-3"
                          >
                            <LinkIcon className="w-3 h-3" />
                            {promo.link.length > 30 ? promo.link.substring(0, 30) + "..." : promo.link}
                          </a>
                        )}
                        
                        {/* Actions */}
                        <div className="flex items-center gap-2 pt-3 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              try {
                                await axios.put(
                                  `${API}/admin/promotions/${promo.id}`,
                                  { is_active: !promo.is_active },
                                  getAuthHeader()
                                );
                                toast.success(`Promotion ${promo.is_active ? "hidden" : "shown"}`);
                                loadDashboardData();
                              } catch (error) {
                                toast.error("Failed to update promotion");
                              }
                            }}
                            className={promo.is_active ? "text-orange-600" : "text-green-600"}
                          >
                            {promo.is_active ? "Hide" : "Show"}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              if (!confirm(`Delete this promotion?`)) return;
                              setDeletingBankPromo(promo.id);
                              try {
                                await axios.delete(`${API}/admin/promotions/${promo.id}`, getAuthHeader());
                                toast.success("Promotion deleted");
                                loadDashboardData();
                              } catch (error) {
                                toast.error("Failed to delete promotion");
                              } finally {
                                setDeletingBankPromo(null);
                              }
                            }}
                            disabled={deletingBankPromo === promo.id}
                            className="text-red-600 hover:text-red-700"
                          >
                            {deletingBankPromo === promo.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Create Bank Promotion Dialog */}
            <Dialog open={showBankPromoDialog} onOpenChange={setShowBankPromoDialog}>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>Add Bank Promotion</DialogTitle>
                  <DialogDescription>
                    Add a promotional banner to display on the homepage carousel
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label htmlFor="bank-name">Bank Name *</Label>
                    <Input
                      id="bank-name"
                      placeholder="e.g., Fidelity Bank"
                      value={newBankPromo.bank_name}
                      onChange={(e) => setNewBankPromo({...newBankPromo, bank_name: e.target.value})}
                      className="mt-1"
                      data-testid="bank-promo-bank-name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="promo-title">Title *</Label>
                    <Input
                      id="promo-title"
                      placeholder="e.g., Special Car Loan Rates"
                      value={newBankPromo.title}
                      onChange={(e) => setNewBankPromo({...newBankPromo, title: e.target.value})}
                      className="mt-1"
                      data-testid="bank-promo-title"
                    />
                  </div>

                  <div>
                    <Label htmlFor="image-url">Image URL *</Label>
                    <Input
                      id="image-url"
                      placeholder="https://example.com/banner.jpg"
                      value={newBankPromo.image_url}
                      onChange={(e) => setNewBankPromo({...newBankPromo, image_url: e.target.value})}
                      className="mt-1"
                      data-testid="bank-promo-image-url"
                    />
                    <p className="text-xs text-gray-500 mt-1">Recommended size: 800x450 pixels (16:9 aspect ratio)</p>
                    
                    {/* Image Preview */}
                    {newBankPromo.image_url && (
                      <div className="mt-2 aspect-[16/9] max-w-[300px] rounded-lg overflow-hidden border">
                        <img 
                          src={newBankPromo.image_url} 
                          alt="Preview" 
                          className="w-full h-full object-cover"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="promo-link">Link URL (optional)</Label>
                    <Input
                      id="promo-link"
                      placeholder="https://bank.com/promotion"
                      value={newBankPromo.link}
                      onChange={(e) => setNewBankPromo({...newBankPromo, link: e.target.value})}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Where should users go when they click on this promotion?</p>
                  </div>

                  <div>
                    <Label htmlFor="display-order">Display Order</Label>
                    <Input
                      id="display-order"
                      type="number"
                      placeholder="0"
                      value={newBankPromo.display_order}
                      onChange={(e) => setNewBankPromo({...newBankPromo, display_order: parseInt(e.target.value) || 0})}
                      className="mt-1 w-24"
                      min="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setShowBankPromoDialog(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      if (!newBankPromo.bank_name.trim()) {
                        toast.error("Please enter a bank name");
                        return;
                      }
                      if (!newBankPromo.title.trim()) {
                        toast.error("Please enter a title");
                        return;
                      }
                      if (!newBankPromo.image_url.trim()) {
                        toast.error("Please enter an image URL");
                        return;
                      }
                      
                      setCreatingBankPromo(true);
                      try {
                        await axios.post(
                          `${API}/admin/promotions`,
                          newBankPromo,
                          getAuthHeader()
                        );
                        toast.success("Bank promotion created!");
                        setShowBankPromoDialog(false);
                        setNewBankPromo({ bank_name: "", title: "", image_url: "", link: "", display_order: 0 });
                        loadDashboardData();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || "Failed to create promotion");
                      } finally {
                        setCreatingBankPromo(false);
                      }
                    }}
                    className="btn-primary"
                    disabled={creatingBankPromo}
                    data-testid="create-bank-promo-submit"
                  >
                    {creatingBankPromo ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Add Promotion
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Bank Connections Tab */}
          <TabsContent value="connections">
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-[#4A4A4A]">Bank Connection Requests</h2>
                  <p className="text-sm text-gray-500">Customers requesting to connect with banks for their loan applications</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="w-3 h-3 bg-yellow-400 rounded-full"></span>
                    <span className="text-gray-600">Pending: {bankConnections.filter(c => c.status === "pending").length}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="w-3 h-3 bg-blue-400 rounded-full"></span>
                    <span className="text-gray-600">Contacted: {bankConnections.filter(c => c.status === "contacted").length}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="w-3 h-3 bg-green-400 rounded-full"></span>
                    <span className="text-gray-600">Completed: {bankConnections.filter(c => c.status === "completed").length}</span>
                  </div>
                </div>
              </div>

              {/* Connections Table */}
              {bankConnections.length === 0 ? (
                <div className="bg-white rounded-xl border p-12 text-center">
                  <Send className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500">No bank connection requests yet.</p>
                </div>
              ) : (
                <div className="bg-white rounded-xl border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Bank</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bankConnections.map((connection) => (
                        <TableRow key={connection.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-[#4A4A4A]">{connection.user_name || "N/A"}</p>
                              <p className="text-xs text-gray-500">App: {connection.application_id?.slice(0, 8)}...</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <a href={`mailto:${connection.user_email}`} className="text-sm text-[#52BFB8] hover:underline flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {connection.user_email}
                              </a>
                              {connection.user_phone && (
                                <a href={`tel:${connection.user_phone}`} className="text-sm text-gray-600 flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {connection.user_phone}
                                </a>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="font-medium text-[#52BFB8]">{connection.bank_name}</span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-gray-600">
                              {new Date(connection.created_at).toLocaleDateString('en-US', { 
                                month: 'short', day: 'numeric', year: 'numeric' 
                              })}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              connection.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                              connection.status === "contacted" ? "bg-blue-100 text-blue-700" :
                              connection.status === "in_progress" ? "bg-purple-100 text-purple-700" :
                              connection.status === "completed" ? "bg-green-100 text-green-700" :
                              "bg-gray-100 text-gray-700"
                            }`}>
                              {connection.status.charAt(0).toUpperCase() + connection.status.slice(1).replace("_", " ")}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={connection.status}
                              onValueChange={async (value) => {
                                setUpdatingConnection(connection.id);
                                try {
                                  await axios.put(
                                    `${API}/admin/bank-connections/${connection.id}/status?status=${value}`,
                                    {},
                                    getAuthHeader()
                                  );
                                  toast.success("Status updated");
                                  loadDashboardData();
                                } catch (error) {
                                  toast.error("Failed to update status");
                                } finally {
                                  setUpdatingConnection(null);
                                }
                              }}
                              disabled={updatingConnection === connection.id}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="contacted">Contacted</SelectItem>
                                <SelectItem value="in_progress">In Progress</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Blog CMS Tab */}
          <TabsContent value="blog">
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-[#4A4A4A]">Blog CMS</h2>
                  <p className="text-sm text-gray-500">Create and manage blog posts for SEO and user education</p>
                </div>
                <Button 
                  onClick={() => {
                    setEditingPost(null);
                    setNewBlogPost({
                      title: "",
                      slug: "",
                      excerpt: "",
                      content: "",
                      category: "Credit Education",
                      featured_image: "",
                      author: "Financify Team",
                      is_published: false,
                      meta_description: "",
                      tags: []
                    });
                    setShowBlogDialog(true);
                  }}
                  className="btn-primary"
                  data-testid="create-blog-post-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Post
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border p-4">
                  <p className="text-sm text-gray-500">Total Posts</p>
                  <p className="text-2xl font-bold text-[#4A4A4A]">{blogPosts.length}</p>
                </div>
                <div className="bg-white rounded-xl border p-4">
                  <p className="text-sm text-gray-500">Published</p>
                  <p className="text-2xl font-bold text-green-600">{blogPosts.filter(p => p.is_published).length}</p>
                </div>
                <div className="bg-white rounded-xl border p-4">
                  <p className="text-sm text-gray-500">Drafts</p>
                  <p className="text-2xl font-bold text-orange-600">{blogPosts.filter(p => !p.is_published).length}</p>
                </div>
              </div>

              {/* Blog Posts Table */}
              {blogPosts.length === 0 ? (
                <div className="bg-white rounded-xl border p-12 text-center">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500 mb-4">No blog posts yet. Create your first one!</p>
                  <Button 
                    onClick={() => setShowBlogDialog(true)}
                    className="btn-primary"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Post
                  </Button>
                </div>
              ) : (
                <div className="bg-white rounded-xl border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Views</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {blogPosts.map((post) => (
                        <TableRow key={post.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-[#4A4A4A] line-clamp-1">{post.title}</p>
                              <p className="text-xs text-gray-400">/blog/{post.slug}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm px-2 py-1 rounded-full bg-[#52BFB8]/10 text-[#3DA8A1]">
                              {post.category}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              post.is_published 
                                ? "bg-green-100 text-green-700" 
                                : "bg-orange-100 text-orange-700"
                            }`}>
                              {post.is_published ? "Published" : "Draft"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-gray-600">{post.views || 0}</span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-gray-600">
                              {new Date(post.created_at).toLocaleDateString('en-US', { 
                                month: 'short', day: 'numeric' 
                              })}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditingPost(post);
                                  setNewBlogPost({
                                    title: post.title,
                                    slug: post.slug,
                                    excerpt: post.excerpt || "",
                                    content: post.content || "",
                                    category: post.category,
                                    featured_image: post.featured_image || "",
                                    author: post.author || "Financify Team",
                                    is_published: post.is_published,
                                    meta_description: post.meta_description || "",
                                    tags: post.tags || []
                                  });
                                  setShowBlogDialog(true);
                                }}
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {post.is_published && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                                  className="text-gray-600 hover:text-gray-700"
                                >
                                  <Globe className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={async () => {
                                  if (!confirm(`Delete "${post.title}"?`)) return;
                                  setDeletingPost(post.id);
                                  try {
                                    await axios.delete(`${API}/admin/blog/posts/${post.id}`, getAuthHeader());
                                    toast.success("Post deleted");
                                    loadDashboardData();
                                  } catch (error) {
                                    toast.error("Failed to delete post");
                                  } finally {
                                    setDeletingPost(null);
                                  }
                                }}
                                disabled={deletingPost === post.id}
                                className="text-red-600 hover:text-red-700"
                              >
                                {deletingPost === post.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>

            {/* Create/Edit Blog Post Dialog */}
            <Dialog open={showBlogDialog} onOpenChange={setShowBlogDialog}>
              <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingPost ? "Edit Blog Post" : "Create Blog Post"}</DialogTitle>
                  <DialogDescription>
                    {editingPost ? "Update your blog post content" : "Write a new blog post for your audience"}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="blog-title">Title *</Label>
                      <Input
                        id="blog-title"
                        placeholder="e.g., Understanding TDSR in The Bahamas"
                        value={newBlogPost.title}
                        onChange={(e) => {
                          const title = e.target.value;
                          const slug = title.toLowerCase()
                            .replace(/[^a-z0-9\s-]/g, '')
                            .replace(/\s+/g, '-')
                            .replace(/-+/g, '-');
                          setNewBlogPost({...newBlogPost, title, slug});
                        }}
                        className="mt-1"
                        data-testid="blog-title-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="blog-slug">URL Slug *</Label>
                      <Input
                        id="blog-slug"
                        placeholder="e.g., understanding-tdsr-bahamas"
                        value={newBlogPost.slug}
                        onChange={(e) => setNewBlogPost({...newBlogPost, slug: e.target.value.toLowerCase().replace(/\s+/g, '-')})}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">/blog/{newBlogPost.slug || "your-post-url"}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="blog-category">Category</Label>
                      <Select
                        value={newBlogPost.category}
                        onValueChange={(value) => setNewBlogPost({...newBlogPost, category: value})}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Credit Education">Credit Education</SelectItem>
                          <SelectItem value="Loan Tips">Loan Tips</SelectItem>
                          <SelectItem value="Mortgage Basics">Mortgage Basics</SelectItem>
                          <SelectItem value="Auto Loans">Auto Loans</SelectItem>
                          <SelectItem value="Financial Planning">Financial Planning</SelectItem>
                          <SelectItem value="News">News</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="blog-author">Author</Label>
                      <Input
                        id="blog-author"
                        placeholder="Financify Team"
                        value={newBlogPost.author}
                        onChange={(e) => setNewBlogPost({...newBlogPost, author: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="blog-excerpt">Excerpt *</Label>
                    <Textarea
                      id="blog-excerpt"
                      placeholder="A brief summary that appears in the blog listing..."
                      value={newBlogPost.excerpt}
                      onChange={(e) => setNewBlogPost({...newBlogPost, excerpt: e.target.value})}
                      className="mt-1"
                      rows={2}
                    />
                    <p className="text-xs text-gray-500 mt-1">{newBlogPost.excerpt.length}/200 characters recommended</p>
                  </div>

                  <div>
                    <Label htmlFor="blog-content">Content *</Label>
                    <Textarea
                      id="blog-content"
                      placeholder="Write your blog post content here. Use ## for headings, **text** for bold, and - for bullet points..."
                      value={newBlogPost.content}
                      onChange={(e) => setNewBlogPost({...newBlogPost, content: e.target.value})}
                      className="mt-1 font-mono text-sm"
                      rows={12}
                      data-testid="blog-content-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Supports: ## Headings, ### Subheadings, **bold**, - bullet points, 1. numbered lists
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="blog-featured-image">Featured Image URL (optional)</Label>
                    <Input
                      id="blog-featured-image"
                      placeholder="https://example.com/image.jpg"
                      value={newBlogPost.featured_image}
                      onChange={(e) => setNewBlogPost({...newBlogPost, featured_image: e.target.value})}
                      className="mt-1"
                    />
                    {newBlogPost.featured_image && (
                      <div className="mt-2 max-w-[200px] rounded-lg overflow-hidden border">
                        <img 
                          src={newBlogPost.featured_image} 
                          alt="Preview" 
                          className="w-full h-auto"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="blog-meta">Meta Description (SEO)</Label>
                    <Textarea
                      id="blog-meta"
                      placeholder="A description for search engines..."
                      value={newBlogPost.meta_description}
                      onChange={(e) => setNewBlogPost({...newBlogPost, meta_description: e.target.value})}
                      className="mt-1"
                      rows={2}
                    />
                    <p className="text-xs text-gray-500 mt-1">{newBlogPost.meta_description.length}/160 characters recommended for SEO</p>
                  </div>

                  <div className="flex items-center gap-3 pt-4 border-t">
                    <input
                      type="checkbox"
                      id="blog-publish"
                      checked={newBlogPost.is_published}
                      onChange={(e) => setNewBlogPost({...newBlogPost, is_published: e.target.checked})}
                      className="w-4 h-4 rounded border-gray-300 text-[#52BFB8] focus:ring-[#52BFB8]"
                    />
                    <Label htmlFor="blog-publish" className="cursor-pointer">
                      Publish immediately (visible on blog page)
                    </Label>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setShowBlogDialog(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      if (!newBlogPost.title.trim()) {
                        toast.error("Please enter a title");
                        return;
                      }
                      if (!newBlogPost.slug.trim()) {
                        toast.error("Please enter a URL slug");
                        return;
                      }
                      if (!newBlogPost.excerpt.trim()) {
                        toast.error("Please enter an excerpt");
                        return;
                      }
                      if (!newBlogPost.content.trim()) {
                        toast.error("Please enter content");
                        return;
                      }
                      
                      setSavingPost(true);
                      try {
                        if (editingPost) {
                          await axios.put(
                            `${API}/admin/blog/posts/${editingPost.id}`,
                            newBlogPost,
                            getAuthHeader()
                          );
                          toast.success("Post updated!");
                        } else {
                          await axios.post(
                            `${API}/admin/blog/posts`,
                            newBlogPost,
                            getAuthHeader()
                          );
                          toast.success("Post created!");
                        }
                        setShowBlogDialog(false);
                        setEditingPost(null);
                        loadDashboardData();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || "Failed to save post");
                      } finally {
                        setSavingPost(false);
                      }
                    }}
                    className="btn-primary"
                    disabled={savingPost}
                    data-testid="save-blog-post-btn"
                  >
                    {savingPost ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {editingPost ? "Update Post" : "Create Post"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <div className="card-base">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Customer Reviews</h2>
                  <p className="text-sm text-gray-500">Manage reviews from customers</p>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="px-3 py-1 rounded-full bg-yellow-100 text-yellow-700">
                    {reviews.filter(r => r.status === "pending").length} Pending
                  </span>
                  <span className="px-3 py-1 rounded-full bg-green-100 text-green-700">
                    {reviews.filter(r => r.status === "approved").length} Approved
                  </span>
                  <span className="px-3 py-1 rounded-full bg-red-100 text-red-700">
                    {reviews.filter(r => r.status === "rejected").length} Rejected
                  </span>
                </div>
              </div>

              {reviews.length === 0 ? (
                <div className="text-center py-12">
                  <Star className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No reviews yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div 
                      key={review.id}
                      className={`p-4 rounded-xl border-2 ${
                        review.status === "pending" 
                          ? "border-yellow-200 bg-yellow-50" 
                          : review.status === "approved"
                          ? "border-green-200 bg-green-50"
                          : "border-red-200 bg-red-50"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4 flex-1">
                          {review.image_url ? (
                            <img 
                              src={review.image_url} 
                              alt={review.reviewer_name}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                              <Users className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-gray-900">{review.reviewer_name}</span>
                              <span className={`px-2 py-0.5 text-xs rounded-full ${
                                review.status === "pending" 
                                  ? "bg-yellow-200 text-yellow-800" 
                                  : review.status === "approved"
                                  ? "bg-green-200 text-green-800"
                                  : "bg-red-200 text-red-800"
                              }`}>
                                {review.status}
                              </span>
                            </div>
                            <div className="flex gap-0.5 mb-2">
                              {[1,2,3,4,5].map((star) => (
                                <Star 
                                  key={star}
                                  className={`w-4 h-4 ${
                                    star <= review.rating 
                                      ? "text-amber-400 fill-amber-400" 
                                      : "text-gray-300"
                                  }`}
                                />
                              ))}
                            </div>
                            <p className="text-gray-700 text-sm">{review.review_text}</p>
                            <p className="text-xs text-gray-500 mt-2">
                              {new Date(review.created_at).toLocaleDateString()} • {review.user_email}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {review.status === "pending" && (
                            <>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={async () => {
                                  setUpdatingReview(review.id);
                                  try {
                                    await axios.put(
                                      `${API}/admin/reviews/${review.id}/status?status=approved`,
                                      {},
                                      getAuthHeader()
                                    );
                                    toast.success("Review approved");
                                    loadDashboardData();
                                  } catch (error) {
                                    toast.error("Failed to approve review");
                                  } finally {
                                    setUpdatingReview(null);
                                  }
                                }}
                                disabled={updatingReview === review.id}
                                data-testid={`approve-review-${review.id}`}
                              >
                                {updatingReview === review.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <CheckCircle2 className="w-4 h-4 mr-1" />
                                    Approve
                                  </>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-300 text-red-600 hover:bg-red-50"
                                onClick={async () => {
                                  setUpdatingReview(review.id);
                                  try {
                                    await axios.put(
                                      `${API}/admin/reviews/${review.id}/status?status=rejected`,
                                      {},
                                      getAuthHeader()
                                    );
                                    toast.success("Review rejected");
                                    loadDashboardData();
                                  } catch (error) {
                                    toast.error("Failed to reject review");
                                  } finally {
                                    setUpdatingReview(null);
                                  }
                                }}
                                disabled={updatingReview === review.id}
                                data-testid={`reject-review-${review.id}`}
                              >
                                Reject
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={async () => {
                              if (!window.confirm("Delete this review?")) return;
                              try {
                                await axios.delete(
                                  `${API}/admin/reviews/${review.id}`,
                                  getAuthHeader()
                                );
                                toast.success("Review deleted");
                                loadDashboardData();
                              } catch (error) {
                                toast.error("Failed to delete review");
                              }
                            }}
                            data-testid={`delete-review-${review.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <div className="card-base">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Uploaded Documents</h2>
                  <p className="text-sm text-gray-500">Review and verify user-uploaded documents</p>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="px-3 py-1 rounded-full bg-yellow-100 text-yellow-700">
                    {documents.filter(d => d.status === "pending").length} Pending
                  </span>
                  <span className="px-3 py-1 rounded-full bg-green-100 text-green-700">
                    {documents.filter(d => d.status === "verified").length} Verified
                  </span>
                  <span className="px-3 py-1 rounded-full bg-red-100 text-red-700">
                    {documents.filter(d => d.status === "rejected").length} Rejected
                  </span>
                </div>
              </div>

              {documents.length === 0 ? (
                <div className="text-center py-12">
                  <Upload className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No documents uploaded yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {documents.map((doc) => (
                    <div 
                      key={doc.id}
                      className={`p-4 rounded-xl border-2 ${
                        doc.status === "pending" 
                          ? "border-yellow-200 bg-yellow-50" 
                          : doc.status === "verified"
                          ? "border-green-200 bg-green-50"
                          : "border-red-200 bg-red-50"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4 flex-1">
                          <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center">
                            <FileText className="w-6 h-6 text-gray-500" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-gray-900 capitalize">
                                {doc.document_type?.replace(/_/g, ' ') || 'Document'}
                              </span>
                              <span className={`px-2 py-0.5 text-xs rounded-full ${
                                doc.status === "pending" 
                                  ? "bg-yellow-200 text-yellow-800" 
                                  : doc.status === "verified"
                                  ? "bg-green-200 text-green-800"
                                  : "bg-red-200 text-red-800"
                              }`}>
                                {doc.status}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">
                              <strong>User:</strong> {doc.user_email || doc.user_id || 'Unknown'}
                            </p>
                            <p className="text-sm text-gray-600">
                              <strong>File:</strong> {doc.file_name || 'N/A'}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Uploaded: {doc.created_at ? new Date(doc.created_at).toLocaleString() : 'N/A'}
                            </p>
                            {doc.admin_notes && (
                              <p className="text-sm text-gray-600 mt-2">
                                <strong>Notes:</strong> {doc.admin_notes}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => viewDocument(doc.id)}
                            className="text-blue-600"
                            data-testid={`view-doc-${doc.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadDocument(doc.id, doc.filename || doc.file_name)}
                            className="text-blue-600"
                          >
                            <FileText className="w-4 h-4 mr-1" />
                            Download
                          </Button>
                          {doc.status === "pending" && (
                            <>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={async () => {
                                  setUpdatingDocument(doc.id);
                                  try {
                                    await axios.put(
                                      `${API}/admin/documents/${doc.id}/status?status=verified`,
                                      {},
                                      getAuthHeader()
                                    );
                                    toast.success("Document verified");
                                    loadDashboardData();
                                  } catch (error) {
                                    toast.error("Failed to verify document");
                                  } finally {
                                    setUpdatingDocument(null);
                                  }
                                }}
                                disabled={updatingDocument === doc.id}
                                data-testid={`verify-doc-${doc.id}`}
                              >
                                {updatingDocument === doc.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <CheckCircle2 className="w-4 h-4 mr-1" />
                                    Verify
                                  </>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-300 text-red-600 hover:bg-red-50"
                                onClick={async () => {
                                  setUpdatingDocument(doc.id);
                                  try {
                                    await axios.put(
                                      `${API}/admin/documents/${doc.id}/status?status=rejected`,
                                      {},
                                      getAuthHeader()
                                    );
                                    toast.success("Document rejected");
                                    loadDashboardData();
                                  } catch (error) {
                                    toast.error("Failed to reject document");
                                  } finally {
                                    setUpdatingDocument(null);
                                  }
                                }}
                                disabled={updatingDocument === doc.id}
                                data-testid={`reject-doc-${doc.id}`}
                              >
                                Reject
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="card-base">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Registered Users</h2>
                  <p className="text-sm text-gray-500">All users who have signed up</p>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700">
                    {usersStats.total} Total
                  </span>
                  <span className="px-3 py-1 rounded-full bg-green-100 text-green-700">
                    {usersStats.verified} Verified
                  </span>
                  <span className="px-3 py-1 rounded-full bg-yellow-100 text-yellow-700">
                    {usersStats.total - usersStats.verified} Unverified
                  </span>
                </div>
              </div>

              {users.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No users registered yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Name</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Email</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Phone</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Signed Up</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user, index) => (
                        <tr key={user.id || index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-[#52BFB8]/10 flex items-center justify-center">
                                <span className="text-sm font-medium text-[#52BFB8]">
                                  {(user.full_name || user.email || '?').charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <span className="font-medium text-gray-900">{user.full_name || '-'}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-600">{user.email}</td>
                          <td className="py-3 px-4 text-gray-600">{user.phone || '-'}</td>
                          <td className="py-3 px-4">
                            {user.email_verified ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
                                <CheckCircle2 className="w-3 h-3" />
                                Verified
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-700">
                                <Clock className="w-3 h-3" />
                                Unverified
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-gray-500 text-sm">
                            {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Sales Demos Tab */}
          <TabsContent value="sales-demos">
            <div className="space-y-6" data-testid="sales-demos-tab">
              <div className="card-base bg-gradient-to-br from-[#52BFB8]/5 via-white to-white border-[#52BFB8]/20">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#52BFB8] to-[#3E9690] flex items-center justify-center flex-shrink-0 shadow-lg">
                    <Sparkles className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#2D3748] mb-1">Live Demo Presentations</h3>
                    <p className="text-sm text-gray-600 max-w-2xl">
                      Pre-built scenarios that walk prospects through the entire Financify flow — &quot;Meet the applicant&quot; → pre-filled qualification form → matched lenders → connect with loan officer. Perfect for sales meetings.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Daniel scenario card */}
                <div className="card-base hover:shadow-lg transition-all">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#52BFB8] to-[#3E9690] text-white font-bold text-xl flex items-center justify-center flex-shrink-0">
                      DR
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-[#2D3748]">Daniel Rolle</h4>
                      <p className="text-xs text-gray-500">32 · Gov&apos;t · Ministry of Education</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[#52BFB8]/10 text-[#3E9690] font-medium">Personal Loan</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">$50K ask</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">Qualified</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 space-y-1 mb-4 bg-gray-50 rounded-xl p-3">
                    <div className="flex justify-between"><span>Monthly income:</span> <strong>$5,200</strong></div>
                    <div className="flex justify-between"><span>Existing debts:</span> <strong>$680/mo</strong></div>
                    <div className="flex justify-between"><span>Requested amount:</span> <strong>$50,000</strong></div>
                  </div>
                  <a
                    href="/demo/daniel"
                    target="_blank"
                    rel="noreferrer"
                    className="btn-primary w-full inline-flex items-center justify-center h-11 text-sm rounded-lg"
                    data-testid="launch-demo-daniel"
                  >
                    Launch Demo
                    <ArrowUpRight className="w-4 h-4 ml-2" />
                  </a>
                </div>

                {/* Sarah scenario card */}
                <div className="card-base hover:shadow-lg transition-all">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white font-bold text-xl flex items-center justify-center flex-shrink-0">
                      SB
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-[#2D3748]">Sarah Bethel</h4>
                      <p className="text-xs text-gray-500">28 · Private · Atlantis Resort</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">Auto Loan</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">$28K ask</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">Qualified</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 space-y-1 mb-4 bg-gray-50 rounded-xl p-3">
                    <div className="flex justify-between"><span>Monthly income:</span> <strong>$4,200</strong></div>
                    <div className="flex justify-between"><span>Existing debts:</span> <strong>$300/mo</strong></div>
                    <div className="flex justify-between"><span>Vehicle:</span> <strong>Toyota Corolla</strong></div>
                  </div>
                  <a
                    href="/demo/sarah"
                    target="_blank"
                    rel="noreferrer"
                    className="btn-primary w-full inline-flex items-center justify-center h-11 text-sm rounded-lg"
                    data-testid="launch-demo-sarah"
                  >
                    Launch Demo
                    <ArrowUpRight className="w-4 h-4 ml-2" />
                  </a>
                </div>

                {/* Marcus scenario card */}
                <div className="card-base hover:shadow-lg transition-all">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 text-white font-bold text-xl flex items-center justify-center flex-shrink-0">
                      MF
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-[#2D3748]">Marcus Ferguson</h4>
                      <p className="text-xs text-gray-500">34 · Gov&apos;t · BPL</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 font-medium">First-Time Mortgage</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 font-medium">$285K</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">Qualified</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 space-y-1 mb-4 bg-gray-50 rounded-xl p-3">
                    <div className="flex justify-between"><span>Monthly income:</span> <strong>$6,800</strong></div>
                    <div className="flex justify-between"><span>Existing debts:</span> <strong>$425/mo</strong></div>
                    <div className="flex justify-between"><span>Property price:</span> <strong>$300,000</strong></div>
                  </div>
                  <a
                    href="/demo/marcus"
                    target="_blank"
                    rel="noreferrer"
                    className="btn-primary w-full inline-flex items-center justify-center h-11 text-sm rounded-lg"
                    data-testid="launch-demo-marcus"
                  >
                    Launch Demo
                    <ArrowUpRight className="w-4 h-4 ml-2" />
                  </a>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Demo Slots Tab */}
          <TabsContent value="demo-slots">
            <div className="space-y-6">
              {/* Availability Settings */}
              <div className="card-base">
                <h3 className="text-lg font-semibold text-[#4A4A4A] mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#52BFB8]" />
                  Weekly Availability
                </h3>
                <p className="text-sm text-gray-500 mb-4">Set which days and hours you are available for meetings. 30-minute slots, max 5 per day.</p>

                {/* Current availability */}
                {demoAvailability.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {demoAvailability.sort((a, b) => a.day_of_week - b.day_of_week).map((a) => {
                      const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
                      return (
                        <div key={a.day_of_week} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-[#4A4A4A] w-24">{dayNames[a.day_of_week]}</span>
                            <span className="text-sm text-gray-500">{a.start_time} — {a.end_time}</span>
                          </div>
                          <Button
                            variant="ghost" size="sm"
                            onClick={async () => {
                              try {
                                await axios.delete(`${API}/admin/demo-availability/${a.day_of_week}`, getAuthHeader());
                                toast.success("Removed");
                                loadDashboardData();
                              } catch { toast.error("Failed to remove"); }
                            }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`remove-avail-${a.day_of_week}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add availability */}
                <div className="flex flex-wrap items-end gap-3 p-4 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <div>
                    <Label className="text-xs mb-1">Day</Label>
                    <select
                      value={newAvail.day_of_week}
                      onChange={e => setNewAvail(a => ({ ...a, day_of_week: parseInt(e.target.value) }))}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      data-testid="avail-day-select"
                    >
                      {["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"].map((d, i) => (
                        <option key={i} value={i}>{d}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs mb-1">Start</Label>
                    <Input type="time" value={newAvail.start_time} onChange={e => setNewAvail(a => ({ ...a, start_time: e.target.value }))} className="w-32" data-testid="avail-start-time" />
                  </div>
                  <div>
                    <Label className="text-xs mb-1">End</Label>
                    <Input type="time" value={newAvail.end_time} onChange={e => setNewAvail(a => ({ ...a, end_time: e.target.value }))} className="w-32" data-testid="avail-end-time" />
                  </div>
                  <Button
                    onClick={async () => {
                      setSavingAvail(true);
                      try {
                        await axios.post(`${API}/admin/demo-availability`, newAvail, getAuthHeader());
                        toast.success("Availability saved");
                        loadDashboardData();
                      } catch { toast.error("Failed to save"); }
                      setSavingAvail(false);
                    }}
                    disabled={savingAvail}
                    className="bg-[#52BFB8] hover:bg-[#3DA8A1] text-white"
                    data-testid="save-avail-btn"
                  >
                    {savingAvail ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                    Add
                  </Button>
                </div>
              </div>

              {/* Blocked Dates */}
              <div className="card-base">
                <h3 className="text-lg font-semibold text-[#4A4A4A] mb-4 flex items-center gap-2">
                  <X className="w-5 h-5 text-red-400" />
                  Blocked Dates
                </h3>
                <p className="text-sm text-gray-500 mb-4">Block specific dates (holidays, unavailable days).</p>
                
                {demoBlockedDates.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {demoBlockedDates.map((b, i) => (
                      <div key={i} className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg text-sm">
                        <span className="text-red-700">{b.date}</span>
                        {b.reason && <span className="text-red-400">({b.reason})</span>}
                        <button
                          onClick={async () => {
                            try {
                              await axios.delete(`${API}/admin/demo-blocked-dates/${b.date}`, getAuthHeader());
                              toast.success("Unblocked");
                              loadDashboardData();
                            } catch { toast.error("Failed"); }
                          }}
                          className="text-red-400 hover:text-red-600"
                          data-testid={`unblock-${b.date}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-end gap-3">
                  <div>
                    <Label className="text-xs mb-1">Date</Label>
                    <Input type="date" value={newBlockDate} onChange={e => setNewBlockDate(e.target.value)} className="w-44" data-testid="block-date-input" />
                  </div>
                  <Button
                    onClick={async () => {
                      if (!newBlockDate) return;
                      try {
                        await axios.post(`${API}/admin/demo-blocked-dates`, { date: newBlockDate }, getAuthHeader());
                        toast.success("Date blocked");
                        setNewBlockDate("");
                        loadDashboardData();
                      } catch { toast.error("Failed"); }
                    }}
                    variant="outline" className="border-red-300 text-red-600 hover:bg-red-50"
                    data-testid="block-date-btn"
                  >
                    Block Date
                  </Button>
                </div>
              </div>

              {/* Bookings */}
              <div className="card-base">
                <h3 className="text-lg font-semibold text-[#4A4A4A] mb-4 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  Upcoming Bookings
                  {demoBookings.length > 0 && (
                    <span className="text-sm font-normal text-gray-500">({demoBookings.length})</span>
                  )}
                </h3>

                {demoBookings.length === 0 ? (
                  <p className="text-sm text-gray-400 py-4 text-center">No bookings yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="py-2 px-3 text-xs font-medium text-gray-500 uppercase">Date & Time</th>
                          <th className="py-2 px-3 text-xs font-medium text-gray-500 uppercase">Name</th>
                          <th className="py-2 px-3 text-xs font-medium text-gray-500 uppercase">Company</th>
                          <th className="py-2 px-3 text-xs font-medium text-gray-500 uppercase">Email</th>
                          <th className="py-2 px-3 text-xs font-medium text-gray-500 uppercase">Phone</th>
                          <th className="py-2 px-3 text-xs font-medium text-gray-500 uppercase">Notes</th>
                          <th className="py-2 px-3 text-xs font-medium text-gray-500 uppercase"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {demoBookings.map((b, i) => (
                          <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-3">
                              <div className="text-sm font-medium text-[#4A4A4A]">{b.date}</div>
                              <div className="text-xs text-[#52BFB8]">{b.time} EST</div>
                            </td>
                            <td className="py-3 px-3 text-sm text-gray-700">{b.name}</td>
                            <td className="py-3 px-3 text-sm text-gray-700">{b.company}</td>
                            <td className="py-3 px-3 text-sm text-[#52BFB8]">{b.email}</td>
                            <td className="py-3 px-3 text-sm text-gray-500">{b.phone || '—'}</td>
                            <td className="py-3 px-3 text-sm text-gray-500 max-w-[200px] truncate">{b.notes || '—'}</td>
                            <td className="py-3 px-3">
                              <Button
                                variant="ghost" size="sm"
                                onClick={async () => {
                                  setDeletingBooking(b.booking_id);
                                  try {
                                    await axios.delete(`${API}/admin/demo-bookings/${b.booking_id}`, getAuthHeader());
                                    toast.success("Booking cancelled");
                                    loadDashboardData();
                                  } catch { toast.error("Failed to cancel"); }
                                  setDeletingBooking(null);
                                }}
                                disabled={deletingBooking === b.booking_id}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                data-testid={`cancel-booking-${b.booking_id}`}
                              >
                                {deletingBooking === b.booking_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* KYC Tab */}
          <TabsContent value="kyc">
            <div className="card-base">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#4A4A4A] flex items-center gap-2">
                  <Fingerprint className="w-5 h-5 text-purple-500" />
                  KYC Applications
                </h3>
                <select
                  value={kycFilter}
                  onChange={e => setKycFilter(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                  data-testid="kyc-filter-select"
                >
                  <option value="">All Statuses</option>
                  <option value="not_started">Not Started</option>
                  <option value="in_progress">In Progress</option>
                  <option value="under_review">Under Review</option>
                  <option value="verified">Verified</option>
                  <option value="failed">Failed</option>
                  <option value="manual_review">Manual Review</option>
                </select>
              </div>

              {kycApplications.filter(a => !kycFilter || a.kyc_status === kycFilter).length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">No KYC applications found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="py-2 px-3 text-xs font-medium text-gray-500 uppercase">Applicant</th>
                        <th className="py-2 px-3 text-xs font-medium text-gray-500 uppercase">App ID</th>
                        <th className="py-2 px-3 text-xs font-medium text-gray-500 uppercase">Sumsub ID</th>
                        <th className="py-2 px-3 text-xs font-medium text-gray-500 uppercase">KYC Status</th>
                        <th className="py-2 px-3 text-xs font-medium text-gray-500 uppercase">Review</th>
                        <th className="py-2 px-3 text-xs font-medium text-gray-500 uppercase">Created</th>
                        <th className="py-2 px-3 text-xs font-medium text-gray-500 uppercase">Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kycApplications
                        .filter(a => !kycFilter || a.kyc_status === kycFilter)
                        .map((app, i) => {
                          const statusColors = {
                            not_started: "bg-gray-100 text-gray-600",
                            in_progress: "bg-blue-50 text-blue-600",
                            under_review: "bg-amber-50 text-amber-600",
                            verified: "bg-green-50 text-green-600",
                            failed: "bg-red-50 text-red-600",
                            manual_review: "bg-purple-50 text-purple-600",
                          };
                          return (
                            <tr key={i} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`kyc-row-${app.application_id}`}>
                              <td className="py-3 px-3">
                                <div className="text-sm font-medium text-[#4A4A4A]">{app.full_name}</div>
                                <div className="text-xs text-gray-400">{app.email}</div>
                              </td>
                              <td className="py-3 px-3 text-xs font-mono text-gray-500">{app.application_id}</td>
                              <td className="py-3 px-3 text-xs font-mono text-gray-500">{app.sumsub_applicant_id || '—'}</td>
                              <td className="py-3 px-3">
                                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${statusColors[app.kyc_status] || "bg-gray-100 text-gray-600"}`}>
                                  {app.kyc_status?.replace(/_/g, ' ')}
                                </span>
                              </td>
                              <td className="py-3 px-3 text-xs text-gray-500">{app.review_answer || '—'}</td>
                              <td className="py-3 px-3 text-xs text-gray-400">{app.created_at ? new Date(app.created_at).toLocaleDateString() : '—'}</td>
                              <td className="py-3 px-3 text-xs text-gray-400">{app.updated_at ? new Date(app.updated_at).toLocaleDateString() : '—'}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Credit Reports Tab */}
          <TabsContent value="credit-reports">
            <div className="card-base">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#4A4A4A] flex items-center gap-2">
                  <FileText className="w-5 h-5 text-orange-500" />
                  CRIF Credit Reports
                </h3>
                <select
                  value={creditReportFilter}
                  onChange={e => setCreditReportFilter(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                  data-testid="credit-report-filter"
                >
                  <option value="">All</option>
                  <option value="pending_submission">Pending Submission</option>
                  <option value="sent_to_bureau">Sent to Bureau</option>
                </select>
              </div>

              {creditReports.filter(r => !creditReportFilter || r.status === creditReportFilter).length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">No credit reports generated yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="py-2 px-3 text-xs font-medium text-gray-500 uppercase">Applicant</th>
                        <th className="py-2 px-3 text-xs font-medium text-gray-500 uppercase">Report ID</th>
                        <th className="py-2 px-3 text-xs font-medium text-gray-500 uppercase">NIB</th>
                        <th className="py-2 px-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="py-2 px-3 text-xs font-medium text-gray-500 uppercase">Created</th>
                        <th className="py-2 px-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {creditReports
                        .filter(r => !creditReportFilter || r.status === creditReportFilter)
                        .map((r, i) => (
                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`credit-report-row-${r.report_id}`}>
                          <td className="py-3 px-3">
                            <div className="text-sm font-medium text-[#4A4A4A]">{r.applicant_name}</div>
                            <div className="text-xs text-gray-400">{r.applicant_email}</div>
                          </td>
                          <td className="py-3 px-3 text-xs font-mono text-gray-500">{r.report_id}</td>
                          <td className="py-3 px-3 text-xs text-gray-500">{r.nib}</td>
                          <td className="py-3 px-3">
                            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                              r.status === "sent_to_bureau"
                                ? "bg-green-50 text-green-600"
                                : "bg-amber-50 text-amber-600"
                            }`}>
                              {r.status === "sent_to_bureau" ? "Sent to Bureau" : "Pending Submission"}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-xs text-gray-400">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-2">
                              <a
                                href={`${API}/admin/credit-reports/${r.report_id}/pdf`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-[#52BFB8] hover:underline font-medium"
                                data-testid={`download-pdf-${r.report_id}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  const adminPwd = sessionStorage.getItem("adminPassword") || password;
                                  if (!adminPwd) return;
                                  window.open(`${API}/admin/credit-reports/${r.report_id}/pdf?x-admin-password=${encodeURIComponent(adminPwd)}`, '_blank');
                                }}
                              >
                                Download
                              </a>
                              {r.status !== "sent_to_bureau" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={async () => {
                                    if (!window.confirm(`Send credit report for ${r.applicant_name} to CRIF Bureau (info.bs@crif.com)?`)) return;
                                    setSendingToBureau(r.report_id);
                                    try {
                                      await axios.post(`${API}/admin/credit-reports/${r.report_id}/send-to-bureau`, {}, getAuthHeader());
                                      toast.success(`Report sent to bureau for ${r.applicant_name}`);
                                      loadDashboardData();
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || "Failed to send");
                                    }
                                    setSendingToBureau(null);
                                  }}
                                  disabled={sendingToBureau === r.report_id}
                                  className="text-xs border-orange-300 text-orange-600 hover:bg-orange-50"
                                  data-testid={`send-bureau-${r.report_id}`}
                                >
                                  {sendingToBureau === r.report_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                                  Send to Bureau
                                </Button>
                              )}
                              {r.status === "sent_to_bureau" && r.sent_at && (
                                <span className="text-[10px] text-green-500">Sent {new Date(r.sent_at).toLocaleDateString()}</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Buying Power Leads Tab */}
          <TabsContent value="buying-power-leads">
            <div className="space-y-4" data-testid="buying-power-leads-panel">
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="card-base">
                  <p className="text-sm text-gray-500 mb-1">Total Leads</p>
                  <p className="text-3xl font-bold text-[#2D3748]" data-testid="bp-total-leads">{buyingPowerStats.total}</p>
                </div>
                <div className="card-base">
                  <p className="text-sm text-gray-500 mb-1">Paid Reports</p>
                  <p className="text-3xl font-bold text-[#52BFB8]" data-testid="bp-paid-leads">{buyingPowerStats.paid}</p>
                </div>
                <div className="card-base">
                  <p className="text-sm text-gray-500 mb-1">Conversion Rate</p>
                  <p className="text-3xl font-bold text-[#2D3748]">
                    {buyingPowerStats.total > 0 ? ((buyingPowerStats.paid / buyingPowerStats.total) * 100).toFixed(1) : "0.0"}%
                  </p>
                </div>
              </div>

              <div className="card-base overflow-x-auto">
                {buyingPowerLeads.length === 0 ? (
                  <p className="text-center text-gray-500 py-12" data-testid="bp-empty">No Buying Power leads yet.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Listing</TableHead>
                        <TableHead>Range</TableHead>
                        <TableHead>Match</TableHead>
                        <TableHead>Paid</TableHead>
                        <TableHead>Captured</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {buyingPowerLeads.map((lead, i) => (
                        <TableRow key={i} data-testid={`bp-lead-row-${i}`}>
                          <TableCell className="font-medium">{lead.name || <span className="text-gray-400">—</span>}</TableCell>
                          <TableCell className="font-medium">{lead.email}</TableCell>
                          <TableCell>{lead.phone || "—"}</TableCell>
                          <TableCell className="max-w-[240px]">
                            {lead.listing_url ? (
                              <a href={lead.listing_url} target="_blank" rel="noreferrer" className="text-[#52BFB8] hover:underline text-xs truncate block">
                                {lead.listing_price ? formatCurrency(lead.listing_price) : "View listing"}
                              </a>
                            ) : (
                              <span className="text-xs text-gray-500">{lead.listing_price ? formatCurrency(lead.listing_price) : "—"}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs">
                            {formatCurrency(lead.calculated_low)} – {formatCurrency(lead.calculated_high)}
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              lead.match_status === "strong" ? "bg-green-100 text-green-700" :
                              lead.match_status === "possible" ? "bg-amber-100 text-amber-700" :
                              "bg-rose-100 text-rose-700"
                            }`}>
                              {lead.match_status}
                            </span>
                          </TableCell>
                          <TableCell>
                            {lead.paid ? (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#52BFB8]/10 text-[#3E9690]">Paid $29.99</span>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-gray-500">{lead.created_at ? formatDate(lead.created_at) : "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>

              <p className="text-xs text-gray-400 text-center">
                Support inquiries: <a href="mailto:support@financify-caribbean.com" className="text-[#3E9690] hover:underline">support@financify-caribbean.com</a>
              </p>
            </div>
          </TabsContent>

          {/* ---------- Estimator Rates (admin) ---------- */}
          <TabsContent value="estimator">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-start justify-between mb-5 gap-3 flex-wrap">
                <div>
                  <h2 className="text-xl font-bold text-[#2D3748] font-cabinet flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-[#52BFB8]" />
                    Closing-Cost Estimator Rates
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Every change bumps the <strong>config version</strong>; estimates record the version they were calculated against.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {estimatorConfig && (
                    <span className="text-xs rounded-full bg-gray-100 text-gray-600 px-2.5 py-1 font-mono">
                      v{estimatorConfig.version}
                    </span>
                  )}
                  <Button
                    onClick={handleSaveEstimatorConfig}
                    disabled={savingEstimator || !Object.keys(estimatorDirty).length}
                    className="bg-[#52BFB8] hover:bg-[#3DA8A1] text-white h-10"
                    data-testid="save-estimator-rates"
                  >
                    {savingEstimator ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save changes
                  </Button>
                </div>
              </div>

              {!estimatorConfig ? (
                <div className="py-12 text-center text-gray-400 text-sm">Loading…</div>
              ) : (
                <EstimatorRateGrid
                  config={estimatorConfig}
                  dirty={estimatorDirty}
                  onChange={(patch) => setEstimatorDirty((d) => ({ ...d, ...patch }))}
                />
              )}

              <div className="mt-6 pt-5 border-t border-gray-100">
                <h3 className="text-sm font-bold text-[#2D3748] mb-2">Per-bank rate overrides</h3>
                <p className="text-xs text-gray-500 mb-3">
                  Set a bank's own closing-fee / insurance %. Blank fields fall back to the global rates above.
                </p>
                <div className="space-y-2">
                  {bankConfig.map((bank) => (
                    <BankRateOverrideRow
                      key={bank.bank_id}
                      bank={bank}
                      onSave={async (patch) => {
                        try {
                          await axios.put(`${API}/admin/config/${bank.bank_id}`, patch, getAuthHeader());
                          toast.success(`${bank.bank_name} rates updated`);
                          loadDashboardData();
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Could not update bank rates.");
                        }
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ---------- Broker Call-backs (admin) ---------- */}
          <TabsContent value="broker-callbacks">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-start justify-between mb-5 gap-3 flex-wrap">
                <div>
                  <h2 className="text-xl font-bold text-[#2D3748] font-cabinet flex items-center gap-2">
                    <Phone className="w-5 h-5 text-[#52BFB8]" />
                    Broker Call-backs
                    <span className="text-xs rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 font-mono">
                      {brokerCallbacksPending} pending
                    </span>
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Tomorrow's schedule plus every call-back the Buying-Power flow has booked. Tick calls off as you complete them.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadDashboardData}
                  className="h-9"
                >
                  Refresh
                </Button>
              </div>

              <CallbackTable
                callbacks={brokerCallbacks}
                updatingId={updatingCallback}
                onUpdateStatus={handleUpdateCallbackStatus}
              />
            </div>
          </TabsContent>
        </Tabs>
          </div>
        </main>
      </div>
    </div>
  );
}

/* ========= Estimator admin sub-components ========= */

function EstimatorRateGrid({ config, dirty, onChange }) {
  const v = (key) => (dirty[key] !== undefined ? dirty[key] : config[key]);
  const num = (key, step = 0.0001, suffix = "") => (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
        {key.replaceAll("_", " ")}{suffix ? ` (${suffix})` : ""}
      </label>
      <Input
        type="number"
        step={step}
        value={v(key) ?? ""}
        onChange={(e) => onChange({ [key]: e.target.value === "" ? null : Number(e.target.value) })}
        className="h-10 mt-1 font-mono text-sm"
        data-testid={`est-cfg-${key}`}
      />
    </div>
  );
  const bool = (key, label) => (
    <label className="flex items-center gap-2 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2">
      <input
        type="checkbox"
        className="w-4 h-4 accent-[#52BFB8]"
        checked={!!v(key)}
        onChange={(e) => onChange({ [key]: e.target.checked })}
        data-testid={`est-cfg-${key}`}
      />
      <span className="text-sm text-[#2D3748]">{label}</span>
    </label>
  );
  return (
    <div className="space-y-6">
      <section>
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Caps & flat fees</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {num("first_time_exemption_cap", 1000, "BSD")}
          {num("appraisal_fee", 10, "BSD")}
        </div>
      </section>
      <section>
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Taxes</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {num("transfer_vat_full_pct", 0.0001)}
          {num("transfer_vat_buyer_share", 0.01)}
          {num("mortgage_vat_pct", 0.0001)}
        </div>
        <div className="mt-3">
          {bool("apply_first_time_to_mortgage_vat", "Apply first-time exemption to mortgage VAT (default OFF)")}
        </div>
      </section>
      <section>
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Legal & bank</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {num("legal_fee_pct", 0.0001)}
          {num("legal_fee_vat_pct", 0.0001)}
          {num("bank_fee_pct", 0.0001)}
        </div>
      </section>
      <section>
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Insurance</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {num("home_insurance_pct", 0.0001)}
          {num("home_insurance_coastal_pct", 0.0001)}
          {num("life_insurance_under_40", 0.0001)}
          {num("life_insurance_40_50", 0.0001)}
          {num("life_insurance_over_50", 0.0001)}
        </div>
        <div className="mt-3">
          {bool("home_insurance_vat_included", "Homeowners insurance quote includes VAT")}
        </div>
      </section>
    </div>
  );
}

function BankRateOverrideRow({ bank, onSave }) {
  const [draft, setDraft] = useState({
    closing_fee_pct: bank.closing_fee_pct ?? "",
    home_insurance_pct: bank.home_insurance_pct ?? "",
    life_insurance_pct: bank.life_insurance_pct ?? "",
  });
  const [saving, setSaving] = useState(false);
  const dirty =
    String(draft.closing_fee_pct) !== String(bank.closing_fee_pct ?? "") ||
    String(draft.home_insurance_pct) !== String(bank.home_insurance_pct ?? "") ||
    String(draft.life_insurance_pct) !== String(bank.life_insurance_pct ?? "");

  const num = (key, label) => (
    <div className="flex-1 min-w-[110px]">
      <label className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{label}</label>
      <Input
        type="number"
        step="0.0001"
        placeholder="(default)"
        value={draft[key]}
        onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
        className="h-9 mt-0.5 font-mono text-xs"
        data-testid={`bank-${bank.bank_id}-${key}`}
      />
    </div>
  );

  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold text-[#2D3748]">{bank.bank_name}</div>
        <Button
          size="sm"
          variant={dirty ? "default" : "outline"}
          onClick={async () => {
            setSaving(true);
            const patch = {};
            for (const k of ["closing_fee_pct", "home_insurance_pct", "life_insurance_pct"]) {
              const raw = draft[k];
              patch[k] = raw === "" || raw === null ? null : Number(raw);
            }
            await onSave(patch);
            setSaving(false);
          }}
          disabled={!dirty || saving}
          className="h-8"
          data-testid={`bank-${bank.bank_id}-save`}
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
        </Button>
      </div>
      <div className="flex gap-2 flex-wrap">
        {num("closing_fee_pct", "Closing fee %")}
        {num("home_insurance_pct", "Home ins %")}
        {num("life_insurance_pct", "Life ins %")}
      </div>
    </div>
  );
}

function CallbackTable({ callbacks, updatingId, onUpdateStatus }) {
  if (!callbacks.length) {
    return <div className="py-12 text-center text-sm text-gray-400">No broker call-backs yet.</div>;
  }
  const todayIso = new Date().toISOString().slice(0, 10);
  const tomorrowIso = new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 10);
  const chip = (status) => {
    const map = {
      pending: "bg-amber-100 text-amber-700",
      completed: "bg-emerald-100 text-emerald-700",
      no_answer: "bg-rose-100 text-rose-700",
      cancelled: "bg-gray-200 text-gray-600",
    };
    return `${map[status] || map.pending} inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold`;
  };
  return (
    <div className="overflow-x-auto" data-testid="broker-callback-table">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>When</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Contact</TableHead>
            <TableHead>Listing</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {callbacks.map((cb) => {
            const status = cb.status || "pending";
            const whenBadge =
              cb.scheduled_date === todayIso ? "today"
              : cb.scheduled_date === tomorrowIso ? "tomorrow"
              : null;
            return (
              <TableRow key={cb.id} data-testid={`callback-row-${cb.id}`}>
                <TableCell>
                  <div className="font-semibold text-[#2D3748] text-sm">{cb.scheduled_date}</div>
                  <div className="text-xs text-gray-500 flex items-center gap-1.5">
                    {cb.scheduled_time}
                    {whenBadge && (
                      <span className="rounded-full bg-[#52BFB8]/10 text-[#3E9690] px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-bold">
                        {whenBadge}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-sm">{cb.name}</TableCell>
                <TableCell className="text-xs text-gray-600">
                  <div>{cb.email}</div>
                  <div className="font-mono">{cb.phone}</div>
                </TableCell>
                <TableCell className="text-xs text-gray-600">
                  {cb.listing_url ? (
                    <a href={cb.listing_url} target="_blank" rel="noreferrer" className="text-[#3E9690] underline break-all">
                      Listing
                    </a>
                  ) : (<span className="text-gray-300">—</span>)}
                  {cb.listing_price ? (
                    <div className="text-[#2D3748] font-semibold">
                      ${Number(cb.listing_price).toLocaleString()}
                    </div>
                  ) : null}
                </TableCell>
                <TableCell><span className={chip(status)}>{status.replace("_", " ")}</span></TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                    {status !== "completed" && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={updatingId === cb.id}
                        onClick={() => onUpdateStatus(cb.id, "completed")}
                        className="h-7 text-[11px] gap-1"
                        data-testid={`callback-${cb.id}-complete`}
                      >
                        <CheckCircle2 className="w-3 h-3 text-emerald-500" /> Done
                      </Button>
                    )}
                    {status === "pending" && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={updatingId === cb.id}
                          onClick={() => onUpdateStatus(cb.id, "no_answer")}
                          className="h-7 text-[11px]"
                        >
                          No answer
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={updatingId === cb.id}
                          onClick={() => onUpdateStatus(cb.id, "cancelled")}
                          className="h-7 text-[11px]"
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                    {status !== "pending" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={updatingId === cb.id}
                        onClick={() => onUpdateStatus(cb.id, "pending")}
                        className="h-7 text-[11px] text-gray-500"
                      >
                        Reopen
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
